import { HeadlineTiny, RichTextContent } from "../../content/components";
import { StaticSiteDataContext } from "../../contexts/StaticSiteDataContext";
import API from "../../core/api";
import { isEmptySlateDocument } from "../../core/constants";
import { getGuestAuthSettings } from "../../core/utils";
import { formatAddress, getEventDateTimeComponent } from "../../events/utils";
import { EventDetail } from "../../events/website/EventDetail";
import { countryNameByCode } from "../../guest-management/forms/constants";
import { Configuration, WebsiteEvent } from "../../resources/modelTypes";
import { ThemedRichText } from "./section-types/ThemedRichText";
import { css } from "@emotion/react";
import {
  ActionLink,
  MediumHeadline,
  SmallHeadline,
} from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo, useContext, useEffect, useState } from "react";

const displayEventStyles = {
  attending: css`
    margin-top: ${theme.spacing.x4};
    margin-bottom: ${theme.spacing.x4};
  `,
  eventInfo: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing.x4};
    max-width: ${styleUtils.rem(780)};
    margin-left: auto;
    margin-right: auto;
    ${theme.media.greaterThan(theme.breakpoints.medium)(`
      flex-direction: row;
    `)};
  `,
  guestName: css`
    list-style-type: none;
  `,
  infoBlock: css`
    width: 100%;
  `,
  infoValue: css`
    margin-top: ${theme.spacing.x3};
  `,
  schedule: css`
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(620)};
  `,
  title: css`
    margin-top: ${theme.spacing.x8};
  `,
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
  `,
};

type VisibleGuest = {
  name: string;
  attending: boolean | null;
  count: number;
};

export type DisplayEventProps = {
  configuration: Configuration;
  event: WebsiteEvent;
};

const DisplayEvent = ({ event }: DisplayEventProps) => {
  const { isOnlineInvitationSite, isWeddingSite } = useContext(
    StaticSiteDataContext,
  );
  const [guests, setGuests] = useState<VisibleGuest[]>([]);
  const [hasLoadedGuestList, setHasLoadedGuestList] = useState(false);

  useEffect(() => {
    if (isOnlineInvitationSite) {
      API.get(
        "guest-management/visible-guest-list",
        getGuestAuthSettings(),
      ).then((data) => {
        setHasLoadedGuestList(true);

        return setGuests(data);
      });
    }
  }, [isOnlineInvitationSite]);

  const formattedAddress = event.hasPhysicalAddress ? formatAddress(event) : "";
  const {
    address1,
    address2,
    city,
    country,
    date,
    description,
    endDate,
    endTime,
    hasPhysicalAddress,
    hostedBy,
    showAttending,
    showNotAttending,
    showNotReplied,
    state,
    time,
    title,
    venue,
    zipCode,
  } = event;

  const attendingGuests = guests.filter((guest) => guest.attending);
  const notAttendingGuests = guests.filter(
    (guest) => guest.attending === false,
  );
  const notRepliedGuests = guests.filter((guest) => guest.attending === null);

  const attendingCount = attendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notAttendingCount = notAttendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notRepliedCount = notRepliedGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);

  return isWeddingSite ? (
    <div>
      <div css={displayEventStyles.title}>
        <SmallHeadline>{title}</SmallHeadline>
      </div>
      <div>
        <EventDetail
          address={formattedAddress}
          country={country}
          date={date}
          endDate={endDate}
          endTime={endTime}
          time={time}
          venue={venue}
        />

        {description && <ThemedRichText content={description} />}

        {hasPhysicalAddress && (
          <ActionLink
            target="_blank"
            text="Get Directions"
            url={"https://www.google.com/maps/dir/?api=1&destination=".concat(
              encodeURIComponent(formattedAddress),
            )}
          />
        )}
      </div>
    </div>
  ) : (
    <div css={displayEventStyles.wrapper}>
      <div css={displayEventStyles.eventInfo}>
        {hostedBy && (
          <div css={displayEventStyles.infoBlock}>
            <HeadlineTiny>Hosted By</HeadlineTiny>
            <div css={displayEventStyles.infoValue}>{hostedBy}</div>
          </div>
        )}
        <div css={displayEventStyles.infoBlock}>
          <HeadlineTiny>Date</HeadlineTiny>
          <div css={displayEventStyles.infoValue}>
            {getEventDateTimeComponent(date, time, endDate, endTime)}
          </div>
        </div>
        {(venue || hasPhysicalAddress) && (
          <div css={displayEventStyles.infoBlock}>
            <HeadlineTiny>Address</HeadlineTiny>
            <div css={displayEventStyles.infoValue}>
              {venue && venue !== address1 && (
                <>
                  {venue}
                  <br />
                </>
              )}
              {hasPhysicalAddress && (
                <>
                  {address1 && (
                    <>
                      {address1}
                      {address2 ? `, ${address2}` : ""}
                      <br />
                    </>
                  )}
                  {city || state || zipCode
                    ? `${city || ""}, ${state || ""} ${zipCode || ""}`
                    : ""}
                  {country && country !== "US" && (
                    <>
                      <br />
                      {countryNameByCode[country]}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {!isEmptySlateDocument(description) && (
        <div>
          <MediumHeadline>Event Schedule</MediumHeadline>
          <div css={displayEventStyles.schedule}>
            <RichTextContent value={description} />
          </div>
        </div>
      )}
      {hasLoadedGuestList &&
        (showAttending || showNotAttending || showNotReplied) && (
          <div>
            <MediumHeadline>Guest List</MediumHeadline>
            {showAttending && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${attendingCount} ATTENDING`}
                </div>
                {attendingGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`attending-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
            {showNotAttending && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${notAttendingCount} NOT ATTENDING`}
                </div>
                {notAttendingGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`notAttending-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
            {showNotReplied && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${notRepliedCount} NOT YET REPLIED`}
                </div>
                {notRepliedGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`not-replied-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
          </div>
        )}
    </div>
  );
};

const MemoizedDisplayEvent = memo(DisplayEvent);

export { MemoizedDisplayEvent as DisplayEvent };
